import { v4 as uuidv4 } from 'uuid';
import L from 'leaflet';
import {
  AREA_TYPES, DEFAULT_LINE_WIDTH, EXCLUSION_TYPES, getAreaTypeColor, getAreaTypeLabel,
} from '../../SiteTakeoff/siteTakeoffConstants';
import { enhancedGetPolygonArea } from '../../MapWithDrawing/Measure/mapUtils';

const DEFAULT_POLYGON_TYPE = AREA_TYPES.BD_SHRUB_MED;
const DEFAULT_LINE_TYPE = AREA_TYPES.TR_DG;
const DEFAULT_POINT_TYPE = AREA_TYPES.POINT_MARKER;

const childrenByLocalName = (node, localName) => (
  Array.from(node?.children || []).filter(child => child.localName === localName)
);

const firstChildText = (node, localName) => (
  childrenByLocalName(node, localName)[0]?.textContent?.trim() || ''
);

const descendantsByLocalName = (node, localName) => (
  Array.from(node?.getElementsByTagName('*') || []).filter(child => child.localName === localName)
);

const firstDescendantText = (node, localName) => (
  descendantsByLocalName(node, localName)[0]?.textContent?.trim() || ''
);

const isFinitePoint = (point) => (
  Number.isFinite(point?.lat) && Number.isFinite(point?.lng)
);

const samePoint = (a, b) => (
  Math.abs(Number(a?.lat) - Number(b?.lat)) < 0.00000001
  && Math.abs(Number(a?.lng) - Number(b?.lng)) < 0.00000001
);

const parseCoordinateText = (coordinateText) => (
  String(coordinateText || '')
    .trim()
    .split(/\s+/)
    .map(token => {
      const [lng, lat] = token.split(',').map(Number);
      return { lat, lng };
    })
    .filter(isFinitePoint)
);

const stripClosedRingDuplicate = (paths) => {
  if (!Array.isArray(paths) || paths.length < 2) return paths || [];
  if (!samePoint(paths[0], paths[paths.length - 1])) return paths;
  return paths.slice(0, -1);
};

const lineLengthFeet = (paths) => {
  if (!paths || paths.length < 2) return 0;
  let totalMeters = 0;
  for (let i = 1; i < paths.length; i += 1) {
    const previous = L.latLng(paths[i - 1].lat, paths[i - 1].lng);
    const current = L.latLng(paths[i].lat, paths[i].lng);
    totalMeters += previous.distanceTo(current);
  }
  return totalMeters * 3.28084;
};

const buildPolygonArea = (name, paths, { id, sourceId } = {}) => {
  const areaType = DEFAULT_POLYGON_TYPE;
  const measurements = enhancedGetPolygonArea(paths);
  return {
    geometryType: 'polygon',
    name: name || getAreaTypeLabel(areaType),
    areaType,
    paths,
    area: (measurements.areaFeet || 0).toFixed(2),
    areaAcres: (measurements.areaAcres || 0).toFixed(4),
    perimeter: (measurements.perimeterFeet || 0).toFixed(2),
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
    ...(id ? { id } : {}),
    // sourceId = the dfl:sourceId we stamped on export; present only for areas
    // that originated in this app and round-tripped through Earth.
    ...(sourceId ? { sourceId } : {}),
  };
};

const buildHoleArea = (parentName, parentId, holePaths, holeIndex) => {
  const name = `${parentName} — hole ${holeIndex + 1}`;
  const areaType = AREA_TYPES.DO_NOT_SERVICE;
  const measurements = enhancedGetPolygonArea(holePaths);
  return {
    geometryType: 'polygon',
    name,
    areaType,
    paths: holePaths,
    area: (measurements.areaFeet || 0).toFixed(2),
    areaAcres: (measurements.areaAcres || 0).toFixed(4),
    perimeter: (measurements.perimeterFeet || 0).toFixed(2),
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
    parentAreaId: parentId,
    exclusionType: EXCLUSION_TYPES.OTHER,
  };
};

const buildPolylineArea = (name, paths) => {
  const areaType = DEFAULT_LINE_TYPE;
  const lengthFeet = lineLengthFeet(paths);
  const areaSF = lengthFeet * DEFAULT_LINE_WIDTH;
  return {
    geometryType: 'polyline',
    name: name || getAreaTypeLabel(areaType),
    areaType,
    paths,
    length: lengthFeet.toFixed(2),
    width: DEFAULT_LINE_WIDTH,
    area: areaSF.toFixed(2),
    areaAcres: (areaSF / 43560).toFixed(4),
    perimeter: '0',
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
  };
};

const buildPointArea = (name, paths) => {
  const areaType = DEFAULT_POINT_TYPE;
  return {
    geometryType: 'point',
    name: name || getAreaTypeLabel(areaType),
    areaType,
    paths,
    area: '0',
    areaAcres: '0',
    perimeter: '0',
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
  };
};

// Read a dfl: field out of a Placemark's <ExtendedData><Data name="...">.
const extendedDataValue = (placemark, dataName) => {
  const extended = descendantsByLocalName(placemark, 'Data');
  const match = extended.find(el => el.getAttribute('name') === dataName);
  return match ? firstDescendantText(match, 'value') : '';
};

const parsePlacemark = (placemark, index) => {
  const fallbackName = `Imported Area ${index + 1}`;
  const name = firstChildText(placemark, 'name') || fallbackName;
  // Identity for round-trip matching, if this Placemark came from our export.
  const sourceId = extendedDataValue(placemark, 'dfl:sourceId') || undefined;
  const imported = [];
  let skipped = 0;

  descendantsByLocalName(placemark, 'Polygon').forEach(polygon => {
    const outerBoundary = descendantsByLocalName(polygon, 'outerBoundaryIs')[0];
    const coordinateText = firstDescendantText(outerBoundary || polygon, 'coordinates');
    const paths = stripClosedRingDuplicate(parseCoordinateText(coordinateText));
    if (paths.length < 3) { skipped += 1; return; }

    // Pre-assign an id so inner-ring holes can reference this parent before addArea runs.
    const parentId = uuidv4();
    imported.push(buildPolygonArea(name, paths, { id: parentId, sourceId }));

    // Parse each inner ring (hole) as an exclusion child linked to the parent.
    const innerBoundaries = descendantsByLocalName(polygon, 'innerBoundaryIs');
    innerBoundaries.forEach((inner, holeIndex) => {
      const holeText = firstDescendantText(inner, 'coordinates');
      const holePaths = stripClosedRingDuplicate(parseCoordinateText(holeText));
      if (holePaths.length >= 3) {
        imported.push(buildHoleArea(name, parentId, holePaths, holeIndex));
      } else {
        skipped += 1;
      }
    });
  });

  descendantsByLocalName(placemark, 'LineString').forEach(line => {
    const paths = parseCoordinateText(firstDescendantText(line, 'coordinates'));
    if (paths.length >= 2) imported.push(buildPolylineArea(name, paths));
    else skipped += 1;
  });

  descendantsByLocalName(placemark, 'Point').forEach(point => {
    const paths = parseCoordinateText(firstDescendantText(point, 'coordinates')).slice(0, 1);
    if (paths.length === 1) imported.push(buildPointArea(name, paths));
    else skipped += 1;
  });

  return { imported, skipped };
};

export const parseKmlToAreaDrafts = (kmlText) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlText, 'application/xml');
  const parseError = descendantsByLocalName(doc, 'parsererror')[0];
  if (parseError) {
    throw new Error('Could not parse KML file.');
  }

  const placemarks = descendantsByLocalName(doc, 'Placemark');
  const imported = [];
  let skipped = 0;

  placemarks.forEach((placemark, index) => {
    const result = parsePlacemark(placemark, index);
    imported.push(...result.imported);
    skipped += result.skipped;
  });

  return {
    areas: imported,
    skipped,
    placemarkCount: placemarks.length,
  };
};

// Geometry-derived fields to copy onto a matched existing area. We deliberately
// exclude name, areaType, colors, and all service metadata — the app is
// authoritative on those; Earth only reshapes geometry.
const GEOMETRY_UPDATE_FIELDS = [
  'paths',
  'area',
  'areaAcres',
  'perimeter',
  'length',
  'width',
];

/**
 * Split parsed KML drafts into updates to existing areas and genuinely new
 * additions, using the dfl:sourceId stamped on export.
 *
 * - A draft whose sourceId matches an existing area's id/_id → an update that
 *   carries ONLY geometry fields (app wins on name/type/metadata).
 * - A draft with no sourceId, or a sourceId that matches nothing → an addition.
 * - Existing areas absent from the KML are left untouched (not returned here).
 *
 * Hole/child drafts (parentAreaId set) always ride along as additions with
 * their parent; matching them individually isn't supported yet.
 *
 * @returns {{ updates: Array<{ areaId: string, updates: object }>, additions: object[] }}
 */
export const reconcileKmlAreas = (draftAreas, existingAreas = []) => {
  const existingById = new Map();
  existingAreas.forEach(area => {
    const id = area?._id || area?.id;
    if (id) existingById.set(String(id), area);
  });

  const updates = [];
  const additions = [];

  draftAreas.forEach(draft => {
    const matchId = draft.sourceId && existingById.has(String(draft.sourceId))
      ? String(draft.sourceId)
      : null;

    // Only top-level areas match; children (holes) follow their parent as adds.
    if (matchId && !draft.parentAreaId) {
      const geometryUpdates = {};
      GEOMETRY_UPDATE_FIELDS.forEach(field => {
        if (draft[field] !== undefined) geometryUpdates[field] = draft[field];
      });
      updates.push({ areaId: matchId, updates: geometryUpdates });
    } else {
      // Drop the transient sourceId so a new area doesn't carry a stale link.
      const { sourceId, ...addition } = draft;
      additions.push(addition);
    }
  });

  return { updates, additions };
};
